import {
	createUploadSessionAsync,
	getUploadSessionAsync,
	IUploadResult,
	IUploadSessionResponse,
	upload,
} from "../../services/upload-service";
import { batchTasks } from "../../utils/batchTasks";
import { getCookie } from "../../utils/cookie";
import { createSessionPoller, ISessionPoller } from "./session-poller";

export interface IUploadOptions {
	onBeforeUploadStart?: () => void;
	onFileUploaded?: (amountUploadedFiles: number) => void;
	onUploadFinished?: (
		folderId: string,
		importedFileIds: string[] | null,
		failedUploadAmount: number,
		failedFileNames: string[] | null,
	) => void;
	flow?: string;
	uploadTechnology?: string;
	batchSize?: number;
}

export interface IUpload {
	readonly isUploading: () => boolean;
}

export async function createUpload(
	files: FileList,
	folderId?: string,
	sessionId?: string,
	opts?: IUploadOptions,
): Promise<IUpload> {
	const failedFileNames: string[] = [];
	let uploadErrors = 0;
	let sessionPoller: ISessionPoller | null = null;
	let uploading = false;

	const accountId = getCookie("accid");

	const {
		onBeforeUploadStart,
		onFileUploaded,
		onUploadFinished,
		flow = "myalbums",
		uploadTechnology = "html5",
		batchSize = 5,
	} = opts;

	const init = async (): Promise<void> => {
		uploading = true;
		let uploadSession: IUploadSessionResponse;
		if (sessionId) {
			uploadSession = await getUploadSessionAsync(sessionId, accountId);
			folderId = uploadSession.folderId;
		} else {
			uploadSession = await createUploadSessionAsync(folderId, flow, uploadTechnology, accountId);
		}

		if (uploadSession) createUploadSessionComplete(uploadSession);
	};

	const createUploadSessionComplete = (session: IUploadSessionResponse) => {
		if (onBeforeUploadStart) onBeforeUploadStart();
		executeUploadsInBatch(session, Array.from(files), batchSize);
	};

	const executeUploadsInBatch = async (session: IUploadSessionResponse, files: File[], batchSize: number) => {
		let fileCount = 1;

		const onBachUploaded = (filesFromBatch: IUploadResult[]) => {
			filesFromBatch.forEach((res) => {
				if (!res.success) {
					uploadErrors++;
					failedFileNames.push(res.file.name);
				}

				if (fileCount === files.length) {
					if (uploadErrors === files.length) {
						onUploadFinished && onUploadFinished(folderId, null, uploadErrors, failedFileNames);
						uploading = false;
					}
				}
				fileCount++;
			});
			if (!sessionPoller) {
				const onPollingFinished = (failedPollingFileNames: string[], pollingErrors: number) => {
					onUploadFinished &&
						onUploadFinished(
							folderId,
							sessionPoller.getImportedFileIds ? sessionPoller.getImportedFileIds() : null,
							(uploadErrors += pollingErrors),
							failedFileNames.concat(failedPollingFileNames),
						);
				};

				sessionPoller = createSessionPoller({ onFileUploaded, onPollingFinished });
				sessionPoller.startSessionPolling(session.id, files.length);
			}
		};

		const tasks = files.map((file) => () => uploadFile(session, file));

		for await (const batch of batchTasks(tasks, batchSize)) {
			onBachUploaded(batch as IUploadResult[]);
		}
	};

	const uploadFile = async (session: IUploadSessionResponse, file: File): Promise<IUploadResult> => {
		return upload(session.uploadUrl + "?uploadId=" + session.id, file);
	};

	const isUploading = () => {
		return uploading;
	};

	await init();

	return {
		isUploading,
	} as IUpload;
}
function CreateSessionPoller(id: string, length: number): ISessionPoller {
	throw new Error("Function not implemented.");
}
